import React, { useState } from 'react';
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native';

import { BadgeShieldIcon, LockIcon } from '../shared/RelicVaultIcons';
import { COLORS, hoverTransition } from '../shared/theme';
import { iconGlowFor } from 'utils/relicVault/colorUtils';
import { IS_WEB, webOnly } from 'utils/relicVault/webStyle';

/**
 * One badge in the grid. The badge name is part of the artwork, so the tile is
 * art only - the name and how to earn it live in the detail popup.
 *
 * Every badge keeps its colour, earned or not: the art is the reward on show,
 * and draining it made the grid look broken rather than unfinished. A locked
 * badge is marked by a gold padlock instead, and a badge that has no artwork
 * yet falls back to a neutral plate.
 */
const BadgeCard = ({ badge, owned, rarityColor, size, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  const imageUrl = badge.imageUrl;

  return (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={() => onSelect(badge.badgeId)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={owned ? badge.name : `${badge.name}, locked`}
      style={[
        styles.card,
        { width: size, height: size },
        hovered && styles.cardHover,
        hoverTransition(120),
      ]}>
      {/* The rarity glow belongs to the artwork - a badge still being drawn
          gets the neutral plate instead, so it never reads as a stray blob. */}
      <View
        style={[
          StyleSheet.absoluteFill,
          styles.glow,
          imageUrl
            ? owned
              ? IS_WEB
                ? { backgroundImage: iconGlowFor(rarityColor) }
                : { backgroundColor: `${rarityColor}22` }
              : styles.glowLocked
            : styles.glowLocked,
        ]}
      />

      {imageUrl ? (
        <Image
          source={{ uri: imageUrl }}
          resizeMode="contain"
          style={[
            styles.art,
            webOnly({ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.45))' }),
          ]}
        />
      ) : (
        <View style={styles.placeholder}>
          <BadgeShieldIcon size={Math.round(size * 0.26)} color="rgba(201,193,240,0.45)" />
        </View>
      )}

      {!owned && !!imageUrl && (
        <View style={styles.lock}>
          <LockIcon
            size={Math.round(size * 0.2)}
            color={COLORS.gold}
            strokeWidth="2.6"
          />
        </View>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardHover: {
    transform: [{ translateY: -2 }],
  },
  glow: {
    borderRadius: 999,
  },
  glowLocked: {
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  art: {
    width: '100%',
    height: '100%',
  },
  // Shown until a badge has artwork: a plate so the grid keeps its shape and
  // the tile still reads as a badge slot rather than an empty cell.
  placeholder: {
    width: '76%',
    height: '76%',
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: 'rgba(255,255,255,0.14)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // A gold padlock, dark enough behind to read against any artwork.
  lock: {
    position: 'absolute',
    width: '34%',
    aspectRatio: 1,
    borderRadius: 999,
    backgroundColor: 'rgba(6,5,18,0.82)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,216,115,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    ...webOnly({ boxShadow: '0 2px 10px rgba(0,0,0,0.5)' }),
  },
});

export default React.memo(BadgeCard);
